/**
 * SortableHeader
 *
 * A clickable jobs-table column header that toggles server-side sorting.
 * Renders inside a <TableHead>; the parent owns the sort state and the
 * three-state toggle logic (asc → desc → cleared), this component is purely
 * presentational + an onSort callback.
 */

import { cn } from '@/shared/lib/cn';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import type { JobListSortBy, JobListSortOrder } from '../api/jobListApi';

interface SortableHeaderProps {
  /** The backend sort field this header controls. */
  column: JobListSortBy;
  label: string;
  /** Currently active sort field, or '' when unsorted. */
  activeColumn: JobListSortBy | '';
  /** Direction of the active sort, or '' when unsorted. */
  activeOrder: JobListSortOrder | '';
  onSort: (column: JobListSortBy) => void;
  className?: string;
}

export function SortableHeader({
  column,
  label,
  activeColumn,
  activeOrder,
  onSort,
  className,
}: SortableHeaderProps) {
  const isActive = activeColumn === column;

  return (
    <button
      type="button"
      onClick={() => onSort(column)}
      className={cn(
        'flex items-center gap-1 font-semibold text-foreground hover:text-primary transition-colors',
        className,
      )}
    >
      {label}
      {isActive && activeOrder === 'asc' && <ChevronUp className="w-4 h-4" aria-hidden />}
      {isActive && activeOrder === 'desc' && <ChevronDown className="w-4 h-4" aria-hidden />}
      {!isActive && <ChevronsUpDown className="w-4 h-4 text-muted-foreground/50" aria-hidden />}
    </button>
  );
}
